import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Phone, Mail, Loader2 } from 'lucide-react';
import { BUSINESS, getImageUrl } from '../config';

/**
 * Page panier partagé — accessible via un lien unique `/panier/{uuid}` envoyé
 * par le portail METERIS (support.meteris.fr) à un client particulier.
 * Le panier est pré-rempli avec les produits discutés par téléphone/email.
 * Le client peut le consulter sans compte et contacter METERIS pour valider.
 */
export default function CartShared() {
  const { cartUuid } = useParams();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch(`/api/carts/${cartUuid}`);
        if (!resp.ok) throw new Error(resp.status === 404 ? 'Panier introuvable ou expiré' : `Erreur ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setCart(data.cart || data);
        // Marquer le panier comme "claimed" (le client l'a ouvert)
        fetch(`/api/carts/${cartUuid}/claim`, { method: 'POST' }).catch(() => {});
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [cartUuid]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-white">
      <Loader2 className="animate-spin" size={32} />
    </div>
  );

  if (error || !cart) return (
    <div className="min-h-screen bg-slate-50 text-white p-8">
      <div className="max-w-3xl mx-auto text-center py-20">
        <ShoppingCart size={48} className="mx-auto mb-4 text-slate-500" />
        <h1 className="font-heading text-2xl uppercase tracking-tight text-primary mb-2">Panier introuvable</h1>
        <p className="text-slate-600 mb-6">{error || 'Ce lien n\'est plus valide ou a expiré.'}</p>
        <Link to="/" className="inline-block bg-secondary hover:bg-secondary-hover px-6 py-3 rounded-sm font-semibold text-primary">
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );

  const items = cart.items || [];
  const totalTTC = items.reduce((sum, it) => sum + (it.unit_price_ttc || 0) * (it.qty || 1), 0);

  return (
    <div className="min-h-screen bg-slate-50 text-white">
      <div className="max-w-5xl mx-auto p-6 md:p-10">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-white mb-6 text-sm">
          <ArrowLeft size={16} /> Retour
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <ShoppingCart size={28} className="text-secondary" />
          <h1 className="font-heading text-3xl uppercase tracking-tight text-primary">Votre sélection METERIS</h1>
        </div>

        {cart.customer_name && (
          <p className="text-slate-600 mb-4">
            Bonjour <b>{cart.customer_name}</b>, voici les produits que nous avons préparés pour vous.
          </p>
        )}
        {cart.commercial_note && (
          <div className="bg-secondary/5 border border-secondary/30 rounded-sm p-4 mb-6 whitespace-pre-wrap">
            {cart.commercial_note}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {items.length === 0 && (
            <div className="p-8 text-center text-slate-600">Panier vide</div>
          )}
          {items.map((it, idx) => {
            const p = it.product || {};
            const lineTotal = (it.unit_price_ttc || 0) * (it.qty || 1);
            return (
              <div key={idx} className="flex items-center gap-4 p-4 border-b border-slate-200 last:border-b-0">
                {p.image_url && (
                  <img src={getImageUrl(p.image_url)} alt={p.name} className="w-20 h-20 object-contain rounded-sm bg-white/5" />
                )}
                <div className="flex-1">
                  <div className="font-semibold text-primary">{p.name}</div>
                  {p.reference && <div className="text-xs text-slate-500 font-mono mt-1">{p.reference}</div>}
                  {it.note && <div className="text-xs text-secondary mt-1">{it.note}</div>}
                </div>
                <div className="text-right">
                  <div className="text-slate-600 text-sm">{it.qty} × {(it.unit_price_ttc || 0).toFixed(2)} €</div>
                  <div className="font-heading font-bold text-lg text-primary">{lineTotal.toFixed(2)} €</div>
                </div>
              </div>
            );
          })}
          {items.length > 0 && (
            <div className="p-4 bg-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Total TTC</span>
              <span className="text-2xl font-bold text-secondary">{totalTTC.toFixed(2)} €</span>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <a href={`tel:${BUSINESS?.phone || '+33144441412'}`} className="flex items-center gap-3 p-5 bg-emerald-600 hover:bg-emerald-700 rounded-sm font-semibold transition">
              <Phone size={20} />
              <div>
                <div>Valider par téléphone</div>
                <div className="text-sm font-normal opacity-90">{BUSINESS?.phone || '+33 1 44 44 14 12'}</div>
              </div>
            </a>
            <a href={`mailto:${BUSINESS?.email || 'contact@meteris.fr'}?subject=Confirmation panier ${cartUuid.slice(0, 8)}&body=Bonjour,%0D%0A%0D%0AJe confirme la commande du panier ${cartUuid}.%0D%0A%0D%0AMerci.`}
              className="flex items-center gap-3 p-5 bg-secondary hover:bg-secondary-hover rounded-sm font-semibold transition">
              <Mail size={20} />
              <div>
                <div>Valider par email</div>
                <div className="text-sm font-normal opacity-90">{BUSINESS?.email || 'contact@meteris.fr'}</div>
              </div>
            </a>
          </div>
        )}

        <div className="mt-10 text-center text-slate-500 text-sm">
          <p>Ce lien est privé et expire après 30 jours. Référence : <span className="font-mono">{cartUuid}</span></p>
        </div>
      </div>
    </div>
  );
}
